import { h } from 'vue';
import { Icon } from '@iconify/vue';
import aliases from '@ui/configs/icons';

const baseColors = {
  white: '#FFFFFF',
  black: '#000000',

  indigo100: '#EFEFFF',
  indigo200: '#D7D2EA',
  indigo300: '#AA9ED2',
  indigo500: '#4022A1',
  indigo700: '#2F1976',

  blue100: '#D6F4FF',
  blue200: '#8BD9FF',
  blue300: '#46BCF5',
  blue500: '#007CCC',
  blue700: '#0063A3',

  green100: '#EAFDF8',
  green200: '#B3F4E2',
  green300: '#66E6C6',
  green500: '#0FB98D',
  green700: '#0B8968',

  navy100: '#DBF1FF',
  navy200: '#99C6F5',
  navy300: '#5B9BE6',
  navy500: '#2E74D6',
  navy700: '#0150B6',

  orange100: '#FFF4EB',
  orange200: '#FFD3B3',
  orange300: '#FFAD73',
  orange500: '#FF6B1A',
  orange700: '#E35C0F',

  pink100: '#FFE8F1',
  pink200: '#FFB5D0',
  pink300: '#FF80AF',
  pink500: '#E63578',
  pink700: '#BF1C5E',

  purple100: '#F8EFFA',
  purple200: '#E5C4EE',
  purple300: '#C579D8',
  purple500: '#9C35B6',
  purple700: '#6C257E',

  red100: '#FFEAEA',
  red200: '#FFB5B5',
  red300: '#FF7A7A',
  red500: '#E63535',
  red700: '#BF1C1C',

  teal100: '#E6F9F8',
  teal200: '#C0F0EE',
  teal300: '#33C2BD',
  teal500: '#008080',
  teal700: '#006F6A',

  yellow100: '#FFF8E6',
  yellow200: '#FFE5B2',
  yellow300: '#FFD073',
  yellow500: '#E6A700',
  yellow700: '#BF8A00',

  grey100: '#FCFCFC',
  grey200: '#FAFAFA',
  grey300: '#F4F4F4',
  grey400: '#EBEBEB',
  grey500: '#CECECE',
  grey600: '#AAAAAA',
  grey700: '#6E6E6E',
  grey800: '#505050',
  grey900: '#1F1F1F',
  
  // Payscale colors
  forestGreen: '#0A4444',
  forestTeal: '#477373',
};

const {
  white,

  indigo100,
  indigo500,
  indigo700,

  blue100,
  blue500,

  green100,
  green500,

  navy700,

  orange500,
  orange700,

  pink500,

  purple500,

  red100,
  red500,

  teal500,

  yellow100,
  yellow300,
  yellow500,

  grey100,
  grey200,
  grey300,
  grey400,
  grey500,
  grey600,
  grey700,
  grey800,
  grey900,

  forestGreen,
  forestTeal,
} = baseColors;

const icons = {
  defaultSet: 'iconify',
  aliases,
  sets: {
    iconify: {
      component: (props) => h(Icon, props),
    },
  },
};

const lightColors = {
  // Base colors
  ...baseColors,

  // Vuetify
  error: red500,
  success: green500,
  warning: yellow500,
  info: blue500,
  primary: indigo500,

  // Basic and Brand Palette
  basicBlue: blue500,
  basicGreen: green500,
  basicIndigo: indigo500,
  basicOrange: orange500,
  basicPink: pink500,
  basicPurple: purple500,
  basicRed: red500,
  basicYellow: yellow500,

  brandPrimaryDark: indigo700,
  brandPrimary: indigo500,

  brandSecondaryDark: orange700,
  brandSecondary: orange500,

  // Background
  bgDefault: white,
  bgInput: grey100,
  bgCanvas: grey200,
  bgDark: grey300, // hovered
  bgBrandDark: indigo700,
  bgBrandDefault: indigo500,
  bgBrandLight: indigo100,
  bgErrorDefault: red500,
  bgErrorLight: red100,
  bgSuccessDefault: green500,
  bgSuccessLight: green100,
  bgWarningDefault: yellow500,
  bgWarningLight: yellow100,
  bgInfoDefault: blue500,
  bgInfoLight: blue100,

  // Icons
  iconBrand: indigo500,
  iconWhite: white,
  iconDark: grey800,
  iconDarkest: grey900,
  iconDefault: grey700,
  iconError: red500,
  iconSuccess: green500,
  iconWarning: yellow500,
  iconInfo: blue500,

  // Stroke
  strokeDefault: grey400,
  strokeDark: grey600,
  strokeError: red500,
  skeleton: grey400,

  // Text
  textBrand: indigo500,
  textWhite: white,
  textDark: grey800,
  textDarkest: grey900,
  textLight: grey700,
  textLighter: grey600,
  textTextMenu: grey800,
  textError: red500,

  // Insights
  sourceInbound: green500,
  sourceInternal: blue500,
  sourceOther: grey500,
  sourceOutbound: red500,
  sourceReferral: yellow300,

  stageApplication: orange500,
  stageAssessment: purple500,
  stageHire: teal500,
  stageOffer: navy700,
  stageScreen: pink500,

  candidateOther: grey500,
  candidateMaleInferred: teal500,
  candidateFemaleInferred: orange500,
  candidateMale: green500,
  candidateFemale: yellow500,
  candidateSubmitted: indigo500,
  candidateDeclined: grey600,
  candidateNotSubmitted: grey500,
  candidateAsian: purple500,
  candidateBlack: blue500,
  candidateHispanic: pink500,
  candidateNativeAm: navy700,
  candidateHawaiian: yellow300,
  candidateTwoOrMore: orange500,
  candidateWhite: teal500,

  // Libraries
  jobscoreHigh: green500,
  jobscoreLow: orange500,
  jobscoreMedium: blue500,
  jobscoreVeryLow: red500,

  documentstatusDraft: blue500,
  documentstatusFlagship: green500,
  documentstatusPublished: yellow500,
  documentstatusTemplate: orange500,

  // Hyperlinks only - accessibility compliant
  linkDefault: '#3366CC',
  linkHover: '#CC3333',
  linkVisited: '#E61895',

  // Payscale colors
  forestGreen,
  forestTeal,
};

const darkOverrides = {
  // Background
  bgDefault: grey900,
  bgCanvas: '#121212',
  bgInput: grey800,
  bgDark: grey700,
  bgBrandLight: indigo700,

  // Text
  textDarkest: white,
  textDark: grey200,
  textTextMenu: grey200,
  textLight: grey400,
  textLighter: grey500,

  // Stroke
  strokeDefault: grey800,
  strokeDark: grey600,
  skeleton: grey800,

  // Icons
  iconDarkest: white,
  iconDark: grey200,
  iconDefault: grey400,
};

export default {
  icons,
  theme: {
    // // uncomment to use system theme
    // defaultTheme: 'system',
    themes: {
      light: {
        colors: lightColors,
      },
      dark: {
        dark: true,
        colors: { ...lightColors, ...darkOverrides },
      },
    },
  },
};
